import actionCreatorFactory from 'typescript-fsa';

import { IELearningParamMap } from 'src/reducers/eLearning';
import { IFetchELearningProgressParam } from 'src/scenario/ELearningScenarioFactory';

/**
 * eラーニング種別
 */
export enum ELearningKind {
	/** 概要 */
	OUTLINE = 0,
	/** タイトル */
	TITLE = 1,
	/** テキスト */
	TEXT = 2,
	/** テスト */
	TEST = 3,
	/** アンケート */
	ENQUETE = 4,
	/** 課題 */
	SUBJECT = 5,
	/** テスト履歴 */
	TESTLOG = 6,
	/** スコームタイトル */
	SCORM_TITLE = 7,
	/** スコーム */
	SCORM = 8,
	/** スコームテストログ */
	SCORM_TEST_LOG = 9,
	/** 最終ページ */
	TERMINAL = 10,
}

/**
 * アンケートタイプ
 */
export type EnqueteType = '1' | '2' | '3';

/**
 * eラーニングツリーアイテム
 */
export interface IIndexTreeItem {
	text: string;
	children: IIndexTreeItem[] | null;
	kind: ELearningKind;
	iconClass: string;
	id: string;
	testID: string | null;
	hintList: { [lang: string]: number[] } | null;
	testTitle: string | null;
	cnt: number;
	retries: number;
	timeLimit: number;
	notMarkFlg: boolean;
	page: number;
	pageCount: number;
	displayStyle: number;
	disableFlg: boolean;
	enqueteType: EnqueteType;
	indexNo: number;
	requiredFlg: boolean;
	userBookMark: number;
	scoType: string | null;
	seqNo: number;
	answerPeriod: string;
	expired: boolean;
	subjectText?: string;
	completeFlg: boolean;
}

/**
 * サーバレスポンスから登録結果とエラーメッセージを取得
 */
export interface IScoringButtonClickSaveResult {
	dbms: null | string;
	tsqId: string;
	pageCount: number;
	userBookMark: number;
	hintList: null | string[];
	thistoryID: number;
	testTitle: string;
	retries: number;
	cnt: number;
	timeLimit: number;
	displayStyle: number;
	topTree: number;
	lastTree: number;
	messageCode: string;
	page: number;
	scoType: null | string;
	seqNo: number;
	itemID: null | string;
	userLocation: null | string;
	examStartTime: null | string;
	notMarkFlg: boolean;
}

const actionCreator = actionCreatorFactory('@eLearning');

/** eラーニングメニュー表示アクションクリエータ */
export const openELearningIndexTree = actionCreator<{
	isOpen: boolean;
}>('OPEN_ELEARNING_INDEX_TREE');

/** eラーニングTopBlock情報設定アクションクリエータ */
export const setELearningTopBlock = actionCreator<{
	careerID: string;
	classID: string;
	resumeTopic: string;
	resumePage: number;
	explanationPage: number;
	lang: string;
	status: number;
	tsqId: string;
	testTitle: string;
	retries: string;
	cnt: string;
	timeLimit: string;
	scorm: boolean;
	lastLocation: string;
}>('SET_ELEARNING_TOP_BLOCK');

/** eラーニングProgressBlock情報設定アクションクリエータ */
export const setELearningProgressBlock = actionCreator<{
	completion: number;
	progress: number;
}>('SET_ELEARNING_PROGRESS_BLOCK');

/** eラーニングTreeBlock情報設定アクションクリエータ */
export const setELearningTreeBlock = actionCreator<{
	careerTitle: string;
	documentName: string;
	indexTree: IIndexTreeItem[];
	preview: boolean;
	mapParam: IELearningParamMap;
	markAfter: boolean;
	/** eラーニングのコンテンツの必須要素の有無フラグ false:無し true:有り */
	requiredItemFlg: boolean;
}>('SET_ELEARNING_TREE_BLOCK');

/** eラーニングTextBlock情報設定アクションクリエータ */
export const setELearningTextBlock = actionCreator<{
	startTime: string;
}>('SET_ELEARNING_TEXT_BLOCK');

/** eラーニングツリーアイテム設定アクションクリエータ */
export const setELearningTreeItem = actionCreator<{
	item: IIndexTreeItem | null;
}>('SET_ELEARNING_TREE_ITEM');

/** eラーニング保留中ツリーアイテム設定アクションクリエータ */
export const setELearningReservedTreeItem = actionCreator<{
	item: IIndexTreeItem;
}>('SET_ELEARNING_RESERVED_TREE_ITEM');

/** eラーニング保留中ツリーアイテム有効化アクションクリエータ */
export const issueELearningReservedTreeItem = actionCreator<{}>(
	'ISSUE_ELEARNING_RESERVED_TREE_ITEM'
);

/** eラーニング保留中ツリーアイテム初期化アクションクリエータ */
export const clearELearningReservedTreeItem = actionCreator(
	'CLEAR_ELEARNING_RESERVED_TREE_ITEM'
);

/** eラーニング更新リザルト初期化アクションクリエータ */
export const clearElearingUpdateResult = actionCreator(
	'CLEAR_ELEARING_UPDATE_RESULT'
);

/** 後日採点判定フラグ初期化アクションクリエータ */
export const setNotMarkCompleteFlg = actionCreator('SET_NOT_MARK_COMPLETE_FLG');

/** eラーニングページ番号設定アクションクリエータ */
export const setELearningPage = actionCreator<{
	page: number;
}>('SET_ELEARNING_PAGE');

/** eラーニング情報初期化アクションクリエータ */
export const clearELearning = actionCreator('CLEAR_ELEARNING');

/** eラーニングヒント表示アクションクリエータ */
export const openELearningHint = actionCreator<{
	isOpen: boolean;
}>('OPEN_ELEARNING_HINT');

/** eラーニング表示項目設定アクションクリエータ */
export const setELearningItemIndex = actionCreator<{
	index: number;
}>('SET_ELEARNING_ITEM_INDEX');

/** eラーニング次項目表示アクションクリエータ */
export const setELearningNextOrTopITem = actionCreator<{}>(
	'SET_ELEARNING_NEXT_OR_TOP_ITEM'
);

/** eラーニングブックマーク位置表示アクションクリエータ */
export const showELearningByBookmark = actionCreator<{
	topicID: string;
	page: number;
}>('SHOW_ELEARNING_BY_BOOKMARK');

/** eラーニングテスト履歴表示アクションクリエータ */
export const setELearningTreeItemTestLog = actionCreator<{
	testID: string;
	thistoryID: number;
	displayStyle: number;
}>('SET_ELEARNING_TREE_ITEM_TEST_LOG');

/** eラーニングデフォルトページ表示アクションクリエータ */
export const resetELearningPage = actionCreator<{}>('RESET_ELEARNING_PAGE');

/** eラーニング終了ダイアログアクションクリエータ */
export const showELearningFinishDialog = actionCreator<{
	isOpen: boolean;
}>('OPEN_ELEARNING_FINISH_DIALOG');

/** eラーニングヒント表示アクションクリエータ */
export const getScoringButtonClickResult = actionCreator<{
	elearingUpdateResult: IScoringButtonClickSaveResult | undefined;
}>('GET_ELEARNING_UPDATE_RESULT');

/** 2019 */
export const setShowFirstTimeDialog = actionCreator<{
	isFirst: number;
}>('SET_ELEARING_SET_SHOW_FIRST_TIME_DIALOG');

export const setShowTree = actionCreator<{
	showTree: boolean;
}>('SET_ELEARING_SET_SHOW_TREE');

export const setScormApi = actionCreator<{
	scormApi: React.RefObject<HTMLIFrameElement>;
}>('SET_SCORMAPI');

/** 終端ページ初期化アクションクリエータ */
export const initTerminal = actionCreator<IFetchELearningProgressParam>(
	'INIT_TERMINAL'
);

/** 目次移動 */
export const elearningComponentUpdated = actionCreator<{
	next?: IIndexTreeItem | null;
	previous?: IIndexTreeItem | null;
	progress: IFetchELearningProgressParam;
}>('ELEARNING_COMPONENT_UPDATED');

/** eラーニングテキストAdmintTV情報設定アクションクリエータ */
export const setELearningTextAdmintResult = actionCreator<{
	admintFlg: string;
	admintUrl: string;
}>('SET_ELEARNING_TEXT_ADMINT_RESULT');

/** eラーニングテキストAdmintTV連携表示制御設定アクションクリエータ */
export const setAdmintFlg = actionCreator<{
	admintFlg: string;
}>('SET_ADMINT_FLG');

/** 次へナビゲーション制御設定アクションクリエータ */
export const setNextBtnDisFlg = actionCreator<{
	nextBtnDisFlg: boolean;
}>('SET_DIASBLE_FLG');



// WEBPACK FOOTER //
// ./src/actions/eLearning.ts